import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Plus, Trash2, Copy, Send, Eye, Edit2, X, FileText } from "lucide-react";
import EmailEditor from "react-email-editor";
import {
  fetchMarketingTemplates,
  fetchMarketingTemplate,
  createMarketingTemplate,
  updateMarketingTemplate,
  deleteMarketingTemplate,
  duplicateMarketingTemplate,
} from "../../../../../../redux/actions/marketingAction";
import { backendClient } from "../../../../../../api/backend";
import { PenLine } from "lucide-react";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const SIGNATURES = {
  saad: {
    label: "Saad Lachhab",
    html: `<div style="font-size:13px;color:#78716c">--</div><strong style="font-size:14px;color:#1c1917">Saad Lachhab</strong><br/><span style="color:#78716c;font-size:13px">Co-Founder, Lawis.ai</span><br/><br/><div style="border-top:1px solid #e7e5e4;padding-top:10px;font-size:13px;color:#78716c;line-height:1.6">E: <a href="mailto:saad.lachhab@lawis.ai" style="color:#c9a96e;text-decoration:none">saad.lachhab@lawis.ai</a><br/>M: +971 5 22 34 86 55<br/>W: <a href="https://www.lawis.ai" style="color:#c9a96e;text-decoration:none">www.lawis.ai</a></div>`,
  },
  unsubscribe: {
    label: "Lien désinscription (RGPD)",
    html: `<div style="border-top:1px solid #e7e5e4;margin-top:24px;padding-top:16px;text-align:center;font-size:11px;color:#a8a29e">Vous recevez cet email car vous figurez dans notre liste de contacts.<br/><a href="{{unsubscribe_url}}" style="color:#a8a29e;text-decoration:underline">Se désinscrire</a></div>`,
  },
};

const TemplateList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { templates, loading } = useSelector((state) => state.marketing);
  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ name: "", subject: "", category: "campaign", is_plain_text: false });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    dispatch(fetchMarketingTemplates());
  }, [dispatch]);

  const [loadingDesign, setLoadingDesign] = useState(false);
  const pendingDesignRef = useRef(null);

  const handleOpenEditor = async (template = null) => {
    pendingDesignRef.current = null;
    setEditingTemplate(template);
    setFormData({
      name: template?.name || "",
      subject: template?.subject || "",
      category: template?.category || "campaign",
      is_plain_text: template?.is_plain_text || false,
    });
    setEditorOpen(true);

    // Fetch full template (with design) when editing
    if (template?.id) {
      setLoadingDesign(true);
      try {
        const full = await dispatch(fetchMarketingTemplate(template.id));
        if (full?.design && Object.keys(full.design).length > 0) {
          // If editor is already ready, load immediately; otherwise store for onReady
          if (editorRef.current?.editor) {
            editorRef.current.editor.loadDesign(full.design);
          } else {
            pendingDesignRef.current = full.design;
          }
        }
      } catch (e) {
        console.error("Failed to load template design:", e);
      } finally {
        setLoadingDesign(false);
      }
    }
  };

  const onEditorReady = useCallback(() => {
    if (pendingDesignRef.current && editorRef.current?.editor) {
      // Load pending design if it was fetched before the editor was ready
      editorRef.current.editor.loadDesign(pendingDesignRef.current);
      pendingDesignRef.current = null;
    } else if (editorRef.current?.editor && !editingTemplate) {
      // New template — load blank design with transparent background
      editorRef.current.editor.loadDesign({
        body: {
          rows: [],
          values: {
            backgroundColor: "transparent",
            backgroundImage: {},
            contentWidth: "600px",
            contentAlign: "center",
            fontFamily: { label: "DM Sans", value: "'DM Sans', Arial, sans-serif" },
            textColor: "#1c1917",
            linkStyle: {
              body: true,
              linkColor: "#c9a96e",
              linkHoverColor: "#b8955a",
              linkUnderline: false,
              linkHoverUnderline: true,
            },
            _meta: { htmlID: "u_body", htmlClassNames: "u_body" },
          },
        },
      });
    }
  }, [editingTemplate]);

  const insertSignature = (key) => {
    const sig = SIGNATURES[key];
    if (!sig || !editorRef.current?.editor) return;
    // Append an HTML row at the bottom of the design
    editorRef.current.editor.exportHtml((data) => {
      const design = data.design;
      if (design?.body?.rows) {
        design.body.rows.push({
          cells: [1],
          values: {
            padding: "0px",
          },
          columns: [{
            contents: [{
              type: "html",
              values: {
                html: sig.html,
                containerPadding: "10px 10px",
              },
            }],
            values: {
              padding: "0px",
            },
          }],
        });
        editorRef.current.editor.loadDesign(design);
      }
    });
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSaveTemplate = async () => {
    setSubmitting(true);
    try {
      const emailEditorEl = editorRef.current;
      let design = {};
      let html = "";

      // Try to get data from Unlayer editor
      if (emailEditorEl?.editor) {
        const exportData = await new Promise((resolve) => {
          emailEditorEl.editor.exportHtml((data) => resolve(data));
        });
        design = exportData.design;
        html = exportData.html;
      }

      const payload = {
        name: formData.name,
        subject: formData.subject,
        category: formData.category,
        is_plain_text: formData.is_plain_text,
        design: design || {},
        html: html || "",
      };

      if (editingTemplate) {
        await dispatch(updateMarketingTemplate(editingTemplate.id, payload));
      } else {
        await dispatch(createMarketingTemplate(payload));
      }
      setEditorOpen(false);
      dispatch(fetchMarketingTemplates());
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = async (id) => {
    try {
      const result = await backendClient.previewMarketingTemplate(id);
      setPreviewHtml(result.html);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Preview failed:", e);
    }
  };

  const handleTestSend = async () => {
    if (selectedId && testEmail) {
      setSubmitting(true);
      try {
        await backendClient.testSendMarketingTemplate(selectedId, testEmail);
        setTestSendOpen(false);
        setTestEmail("");
      } catch (e) {
        console.error("Test send failed:", e);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const items = templates || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_email_templates')} ({items.length})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenEditor()}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          {t('mkt_new_template')}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
      {loading && items.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {t('mkt_no_templates')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {items.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }} noWrap>
                      {template.name}
                    </Typography>
                    {template.subject && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {template.subject}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {template.is_plain_text && (
                      <Chip label="Plain Text" size="small" sx={{ borderRadius: "6px", fontSize: "0.6rem", height: 18, bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main }} />
                    )}
                    {template.category && (
                      <Chip label={template.category} size="small" sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                    )}
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 1.5 }}>
                  {new Date(template.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEditor(template)}>
                      <Edit2 size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Preview">
                    <IconButton size="small" onClick={() => handlePreview(template.id)}>
                      <Eye size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Test Send">
                    <IconButton size="small" onClick={() => { setSelectedId(template.id); setTestSendOpen(true); }}>
                      <Send size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <IconButton size="small" onClick={() => dispatch(duplicateMarketingTemplate(template.id))}>
                      <Copy size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(template)}
                    >
                      <Trash2 size={14} color={theme.palette.error?.main} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      </Box>

      {/* Template Editor Dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullScreen>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem", whiteSpace: "nowrap" }}>
              {editingTemplate ? t('mkt_edit_template') : t('mkt_new_template')}
            </Typography>
            <TextField
              label={t('mkt_template_name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              size="small"
              sx={{ minWidth: 250 }}
            />
            <TextField
              label={t('mkt_default_subject')}
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              size="small"
              sx={{ minWidth: 250 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button onClick={() => setEditorOpen(false)} size="small">Cancel</Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveTemplate}
              disabled={!formData.name || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            >
              {submitting ? t('mkt_saving') : t('mkt_save_template')}
            </Button>
            <IconButton onClick={() => setEditorOpen(false)} size="small"><X size={18} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Insert Signatures Toolbar */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 2, py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.divider, 0.15),
            flexShrink: 0,
          }}>
            <PenLine size={14} color={theme.palette.text.secondary} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mr: 0.5 }}>
              {t('mkt_insert')}
            </Typography>
            {Object.entries(SIGNATURES).map(([key, sig]) => (
              <Chip
                key={key}
                label={sig.label}
                size="small"
                onClick={() => insertSignature(key)}
                sx={{
                  borderRadius: "6px",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                }}
              />
            ))}
            {/* Plain Text toggle — right side */}
            <Box sx={{ ml: "auto" }}>
              <Chip
                icon={<FileText size={13} />}
                label="Plain Text"
                size="small"
                onClick={() => setFormData(prev => ({ ...prev, is_plain_text: !prev.is_plain_text }))}
                sx={{
                  borderRadius: "6px",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  bgcolor: formData.is_plain_text
                    ? alpha(theme.palette.success.main, 0.15)
                    : alpha(theme.palette.text.secondary, 0.08),
                  color: formData.is_plain_text
                    ? theme.palette.success.main
                    : theme.palette.text.secondary,
                  border: `1px solid ${formData.is_plain_text
                    ? alpha(theme.palette.success.main, 0.4)
                    : alpha(theme.palette.text.secondary, 0.2)}`,
                  "&:hover": {
                    bgcolor: formData.is_plain_text
                      ? alpha(theme.palette.success.main, 0.25)
                      : alpha(theme.palette.text.secondary, 0.15),
                  },
                }}
              />
            </Box>
          </Box>
          {loadingDesign && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <EmailEditor
              ref={editorRef}
              onReady={onEditorReady}
              minHeight="100%"
              options={{
                appearance: {
                  theme: "modern_dark",
                },
                designMode: "email",
                displayMode: "email",
                features: {
                  stockImages: {
                    enabled: true,
                    safeSearch: true,
                  },
                  preheaderText: true,
                },
                mergeTags: {
                  first_name: {
                    name: "Prénom",
                    value: "{{first_name}}",
                    sample: "Saad",
                  },
                  last_name: {
                    name: "Nom",
                    value: "{{last_name}}",
                    sample: "Lachhab",
                  },
                  email: {
                    name: "Email",
                    value: "{{email}}",
                    sample: "saad@lawis.ai",
                  },
                  company: {
                    name: "Entreprise",
                    value: "{{company}}",
                    sample: "Lawis Inc.",
                  },
                  phone: {
                    name: "Téléphone",
                    value: "{{phone}}",
                    sample: "+971522348655",
                  },
                  unsubscribe_url: {
                    name: "Lien désinscription",
                    value: "{{unsubscribe_url}}",
                    sample: "#",
                  },
                  landing_url: {
                    name: "Lien site web",
                    value: "{{landing_url}}",
                    sample: '<a href="https://www.lawis.ai/">lawis.ai</a>',
                  },
                  calendar_fz: {
                    name: "Calendrier FZ",
                    value: "{{calendar_fz}}",
                    sample: '<a href="#">Prendre rendez-vous</a>',
                  },
                  calendar_saad: {
                    name: "Calendrier Saad",
                    value: "{{calendar_saad}}",
                    sample: '<a href="#">Prendre rendez-vous</a>',
                  },
                },
                mergeTagsConfig: {
                  autocompleteTriggerChar: "{",
                  sort: false,
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
          {t('mkt_template_preview')}
          <IconButton onClick={() => setPreviewOpen(false)} size="small"><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              overflow: "hidden",
              minHeight: 400,
              bgcolor: "#ffffff",
              p: 3,
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={testSendOpen} onClose={() => setTestSendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTestSendOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTestSend} disabled={!testEmail || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? "Sending..." : "Send Test"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingTemplate(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name}
        itemType={t("mkt_template")}
      />
    </Box>
  );
};

export default TemplateList;
